import type { Viewer, WireBook } from '@alphadesk/web/contract.ts';
import type { RouteAt } from '@alphadesk/web/links.ts';
import { type ComponentType, type LazyExoticComponent, lazy, Suspense } from 'react';
import { LinkTabs } from '../components/pages/Page.tsx';
import type { HomeState } from '../lib/home.ts';
import type { useStream } from '../lib/stream.ts';

export interface RouteProps {
  viewer: Viewer;
  /** The socket's state, its books filled from the session until the socket sends its own. */
  stream: ReturnType<typeof useStream>;
  books: readonly WireBook[];
  home: HomeState;
  at: RouteAt;
}

export type RouteComponent<P = RouteProps> = LazyExoticComponent<ComponentType<P>>;

/** A page in its own chunk: `lazyPage(() => import('…').then((m) => m.SomePage))`. */
export function lazyPage<P>(load: () => Promise<ComponentType<P>>): RouteComponent<P> {
  return lazy(() => load().then((page) => ({ default: page })));
}

export function Opening() {
  return <p className="text-xs text-ink-faint">Loading…</p>;
}

export type TabPage = RouteComponent<{ viewer: Viewer }>;

interface Tab<K extends string> {
  key: K;
  href: string;
  label: string;
}

export function Tabbed<K extends string>({
  label,
  tabs,
  here,
  Page,
  viewer,
}: {
  label: string;
  tabs: readonly Tab<K>[];
  here: K;
  Page: TabPage;
  viewer: Viewer;
}) {
  return (
    <div className="gap-page flex w-full flex-col">
      <LinkTabs
        label={label}
        tabs={tabs.map((tab) => ({ href: tab.href, here: tab.key === here, label: tab.label }))}
      />
      <Suspense fallback={<Opening />}>
        <Page viewer={viewer} />
      </Suspense>
    </div>
  );
}
